const _getCartToken = () => {
  const m = document.cookie.match(/(?:^|;\s*)cart=([^;]*)/);
  if (!m) return null;
  // 截掉 ?key=... 后缀，只保留纯 token
  return decodeURIComponent(m[1]).split('?')[0];
};


/**
 * section-renderer.js
 *
 * A lightweight, framework-free Section Rendering API helper.
 * Mirrors the design of Horizon's SectionRenderer but runs as a plain
 * browser script with no module bundler required.
 *
 * Usage:
 *   // Fetch raw HTML (no DOM mutation)
 *   const html = await window.SectionRenderer.getSectionHTML('my-section', {
 *     url: new URL('/products/my-product', location.href),
 *     cache: false,
 *   });
 *
 *   // Fetch + replace the matching #shopify-section-<id> element in the DOM
 *   await window.SectionRenderer.renderSection('my-section', {
 *     url: new URL('/products/my-product', location.href),
 *   });
 *
 * Both methods accept the same options object:
 *   url   {URL}     — page URL to render the section for (default: current page)
 *   cache {boolean} — use in-memory cache (default: true outside design mode)
 */

(function () {
  'use strict';

  // ── helpers ──────────────────────────────────────────────────────────────

  const PREFIX = 'shopify-section-';

  /**
   * Strip the "shopify-section-" prefix if present.
   * @param {string} id
   * @returns {string}
   */
  function normalizeSectionId(id) {
    return id.startsWith(PREFIX) ? id.slice(PREFIX.length) : id;
  }

  /**
   * Build the Section Rendering API URL.
   * Appends `?section_id=<id>` (sorted to improve cache key stability).
   * @param {string} sectionId
   * @param {URL} [baseUrl]
   * @returns {string}
   */
  function buildSectionURL(sectionId, baseUrl) {
    const url = new URL(baseUrl || window.location.href);
    url.searchParams.set('section_id', normalizeSectionId(sectionId));
    url.searchParams.sort(); // keeps identical requests as one cache key
    return url.toString();
  }

  // ── SectionRenderer class ─────────────────────────────────────────────────

  class SectionRenderer {
    constructor() {
      /** @type {Map<string, string>} url → cached html */
      this._cache = new Map();

      /** @type {Map<string, Promise<string>>} url → in-flight fetch promise */
      this._pending = new Map();

      /** @type {Map<string, AbortController>} sectionId → abort controller */
      this._abortControllers = new Map();

      // Seed cache from already-rendered page sections on load
      window.addEventListener('load', this._cachePageSections.bind(this), { once: true });
    }

    // ── public API ──────────────────────────────────────────────────────────

    /**
     * Fetch the rendered HTML for a section and replace the matching
     * `#shopify-section-<id>` element in the DOM via innerHTML swap.
     *
     * Cancels any in-progress render for the same section before starting.
     *
     * @param {string} sectionId
     * @param {object}  [options]
     * @param {URL}     [options.url]   - page URL to render for (default: current page)
     * @param {boolean} [options.cache] - use in-memory response cache
     * @returns {Promise<string>} the raw section HTML
     */
    async renderSection(sectionId, options) {
      this._abortPending(sectionId);

      const ac = new AbortController();
      this._abortControllers.set(sectionId, ac);

      let html = '';
      try {
        html = await this.getSectionHTML(sectionId, options, ac.signal);
      } catch (err) {
        if (ac.signal.aborted) return html;
        throw err;
      } finally {
        if (this._abortControllers.get(sectionId) === ac) {
          this._abortControllers.delete(sectionId);
        }
      }

      if (!ac.signal.aborted) {
        this._morphSection(sectionId, html);
      }

      return html;
    }

    /**
     * Fetch raw HTML for a section without touching the DOM.
     *
     * @param {string}  sectionId
     * @param {object}  [options]
     * @param {URL}     [options.url]   - page URL to render for (default: current page)
     * @param {boolean} [options.cache] - use in-memory response cache (default: !Shopify.designMode)
     * @param {AbortSignal} [signal]    - optional abort signal
     * @returns {Promise<string>}
     */
    async getSectionHTML(sectionId, options, signal) {
      const useCache = options?.cache ?? (typeof Shopify !== 'undefined' ? !Shopify.designMode : true);
      const sectionURL = buildSectionURL(sectionId, options?.url);

      if (useCache) {
        const cached = this._cache.get(sectionURL);
        if (cached != null) return cached;
      }

      // Deduplicate simultaneous identical fetches
      let pending = this._pending.get(sectionURL);
      if (pending) return pending;

      const fetchPromise = fetch(sectionURL, {
        signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((r) => {
          if (!r.ok) throw new Error(`SectionRenderer: ${r.status} for ${sectionURL}`);
          return r.text();
        })
        .then((html) => {
          this._cache.set(sectionURL, html);
          return html;
        })
        .finally(() => {
          this._pending.delete(sectionURL);
        });

      this._pending.set(sectionURL, fetchPromise);
      return fetchPromise;
    }

    /**
     * Invalidate the cached HTML for a section (all URL variants).
     * @param {string} sectionId
     */
    invalidate(sectionId) {
      const normalized = normalizeSectionId(sectionId);
      for (const key of this._cache.keys()) {
        // URL contains "section_id=<id>"
        if (key.includes('section_id=' + normalized)) {
          this._cache.delete(key);
        }
      }
    }

    // ── private ─────────────────────────────────────────────────────────────

    /**
     * Replace the inner content of the matching shopify-section wrapper.
     * Falls back to outerHTML swap when the wrapper is absent.
     * @param {string} sectionId
     * @param {string} html
     */
    _morphSection(sectionId, html) {
      const wrapperId = PREFIX + normalizeSectionId(sectionId);
      const existing = document.getElementById(wrapperId);

      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const incoming = fragment.getElementById(wrapperId);

      if (existing && incoming) {
        existing.innerHTML = incoming.innerHTML;
        return;
      }

      // Section not yet in DOM (e.g. injected modal) — caller handles placement
    }

    /** Abort any in-progress render for `sectionId`. */
    _abortPending(sectionId) {
      const ac = this._abortControllers.get(sectionId);
      if (ac) {
        ac.abort();
        this._abortControllers.delete(sectionId);
      }
    }

    /**
     * On page load, seed the cache with the HTML of every section already
     * present in the DOM so the first re-render is instant.
     */
    _cachePageSections() {
      document.querySelectorAll('.shopify-section').forEach((el) => {
        const url = buildSectionURL(el.id);
        if (!this._cache.has(url)) {
          this._cache.set(url, el.outerHTML);
        }
      });
    }
  }

  // ── singleton export ──────────────────────────────────────────────────────

  window.SectionRenderer = new SectionRenderer();
  // Also expose the class for consumers that need custom instances
  window.SectionRendererClass = SectionRenderer;
})();



/**
 * section-modal-trigger.js
 *
 * <section-modal-trigger> — Web Component
 *
 * Clicking any child element (or the host itself) fetches a Shopify section
 * via the Section Rendering API and injects the result into a DSM modal's
 * content area, then opens the modal.
 *
 * Attributes:
 *   section-id      {string}  — Shopify section ID to render (required)
 *   modal-id        {string}  — DSM modal ID to open (required)
 *   src             {string}  — base URL to render the section for
 *                               (default: current page URL)
 *   content-sel     {string}  — CSS selector within fetched HTML to extract
 *                               as content (default: extracts inner HTML of
 *                               the #shopify-section-<id> wrapper)
 *   cache           {string}  — "true" | "false"  (default: "true")
 *   loading-class   {string}  — class applied to modal while loading
 *                               (default: "is-loading")
 *   target-sel      {string}  — CSS selector inside the modal overlay where
 *                               fetched HTML is injected (default: resolves
 *                               .dsm-modal__content → .dsm-modal__body → overlay root)
 *
 * HTML example:
 *   {{ 'section-renderer.js'       | asset_url | script_tag }}
 *   {{ 'section-modal-trigger.js'  | asset_url | script_tag }}
 *
 *   <section-modal-trigger
 *     section-id="bgg-variant-modal"
 *     modal-id="switch-specs-modal"
 *     src="{{ pdp_gift_product.url }}"
 *   >
 *     <button type="button">Switch Specs</button>
 *   </section-modal-trigger>
 *
 *   {% render 'dsm-modal', id: 'switch-specs-modal', title: 'Switch Specs', ... %}
 *
 * Dynamic URL (override per-click):
 *   You can set `element.src = '/products/other-handle'` in JS before or
 *   after mount to change the fetch target without re-rendering Liquid.
 *
 * Events (dispatched on the host element, bubble):
 *   section-modal:before-fetch  — before network request starts
 *                                 detail: { sectionId, modalId, url }
 *   section-modal:after-fetch   — after HTML injected, before modal opens
 *                                 detail: { sectionId, modalId, html }
 *   section-modal:error         — fetch or injection failed
 *                                 detail: { sectionId, modalId, error }
 */




// ── Helpers ──────────────────────────────────────────────────────────────

/**
 * Extract the inner HTML of `#shopify-section-<id>` from a raw section
 * rendering response string, or fall back to the full response.
 * @param {string} html
 * @param {string} sectionId
 * @param {string|null} contentSel
 * @returns {string}
 */
function extractContent(html, sectionId, contentSel) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');

  if (contentSel) {
    const el = fragment.querySelector(contentSel);
    return el ? el.innerHTML : html;
  }

  // Default: inner HTML of the shopify-section wrapper
  const wrapper = fragment.getElementById('shopify-section-' + sectionId);
  return wrapper ? wrapper.innerHTML : html;
}

/**
 * Find the DSM modal overlay element by id.
 * The overlay has `id="${modalId}-overlay"` per dsm-modal.liquid conventions.
 * @param {string} modalId
 * @returns {HTMLElement|null}
 */
function getModalOverlay(modalId) {
  return (
    document.getElementById(modalId + '-overlay') ||
    document.querySelector(`[data-dsm-dialog="${CSS.escape(modalId)}"]`)
  );
}

/**
 * Inject `html` into the target modal's content area.
 * Resolution order:
 *   1. `targetSel` attribute on the trigger (e.g. `target-sel=".my-slot"`)
 *   2. `.dsm-modal__content` inside the overlay
 *   3. `.dsm-modal__body` inside the overlay
 *   4. the overlay root itself
 * @param {HTMLElement} overlay
 * @param {string} html
 * @param {string|null} targetSel
 */
function injectIntoModal(overlay, html, targetSel) {
  const target = targetSel && overlay.querySelector(targetSel);

  target.innerHTML = html;
}

// ── Custom Element ───────────────────────────────────────────────────────

class SectionModalTrigger extends HTMLElement {
  connectedCallback() {
    this._ac = new AbortController();
    this.addEventListener('click', this._handleClick.bind(this), {
      signal: this._ac.signal,
    });
  }

  disconnectedCallback() {
    this._ac?.abort();
  }

  // ── attribute accessors ──────────────────────────────────────────────

  get sectionId() { return this.getAttribute('section-id') || ''; }
  get modalId() { return this.getAttribute('modal-id') || ''; }
  get src() { return this.getAttribute('src') || window.location.href; }
  set src(v) { this.setAttribute('src', v); }
  get contentSel() { return this.getAttribute('content-sel') || null; }
  get useCache() { return this.getAttribute('cache') !== 'false'; }
  get loadingClass() { return this.getAttribute('loading-class') || 'is-loading'; }
  get targetSel() { return this.getAttribute('target-sel') || null; }
  /** 额外覆盖到 fetch URL 的参数，格式同 URLSearchParams（e.g. "variant=12345&color=red"） */
  get urlParameters() { return this.getAttribute('data-params') || ''; }

  // ── click handler ────────────────────────────────────────────────────

  async _handleClick(e) {
    // Ignore clicks on interactive children that handle themselves
    if (e.target.closest('a[href], button[type="submit"], input, select, textarea')) return;

    e.preventDefault();

    const { sectionId, modalId } = this;
    if (!sectionId || !modalId) {
      console.warn('<section-modal-trigger>: section-id and modal-id are required');
      return;
    }

    const overlay = getModalOverlay(modalId);
    if (!overlay) {
      console.warn(`<section-modal-trigger>: modal "${modalId}" not found in DOM`);
      return;
    }

    this.dispatchEvent(
      new CustomEvent('section-modal:before-fetch', {
        bubbles: true,
        detail: { sectionId, modalId, url: this.src },
      })
    );

    overlay.classList.add(this.loadingClass);
    this.classList.add('is-loading');

    let html;
    try {
      html = await this._fetchSection(sectionId);
    } catch (err) {
      overlay.classList.remove(this.loadingClass);
      this.classList.remove('is-loading');
      this.dispatchEvent(
        new CustomEvent('section-modal:error', {
          bubbles: true,
          detail: { sectionId, modalId, error: err },
        })
      );
      console.error('<section-modal-trigger> fetch error:', err);
      return;
    }

    const content = extractContent(html, sectionId, this.contentSel);
    injectIntoModal(overlay, content, this.targetSel);
    overlay.classList.remove(this.loadingClass);
    this.classList.remove('is-loading');

    this.dispatchEvent(
      new CustomEvent('section-modal:after-fetch', {
        bubbles: true,
        detail: { sectionId, modalId, html: content },
      })
    );

    this._openModal(modalId);
  }

  // ── network ──────────────────────────────────────────────────────────

  _fetchSection(sectionId) {
    const fetchUrl = this._buildFetchUrl();
    const renderer = window.SectionRenderer;
    if (renderer) {
      return renderer.getSectionHTML(sectionId, {
        url: fetchUrl,
        cache: this.useCache,
      });
    }

    // Fallback: raw fetch (when section-renderer.js is not loaded)
    fetchUrl.searchParams.set('section_id', sectionId);
    fetchUrl.searchParams.sort();
    return fetch(fetchUrl.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).then((r) => {
      if (!r.ok) throw new Error(`section-modal-trigger: HTTP ${r.status}`);
      return r.text();
    });
  }

  /** 将 src + data-params 合并成一个 URL 对象 */
  _buildFetchUrl() {
    const url = new URL(this.src, window.location.origin);
    const extra = this.urlParameters;
    if (extra) {
      new URLSearchParams(extra).forEach((v, k) => url.searchParams.set(k, v));
    }
    return url;
  }

  // ── open modal ───────────────────────────────────────────────────────

  _openModal(modalId) {
    if (window.DsmDialog) {
      window.DsmDialog.open(modalId);
      return;
    }
    // Fallback: set `hidden` attribute to false on the overlay
    const overlay = getModalOverlay(modalId);
    if (overlay) overlay.removeAttribute('hidden');
  }

  // ── Public API ───────────────────────────────────────────────────────

  /**
   * Programmatically trigger the same fetch + open flow.
   * Useful when you want to pre-load content on hover or other events.
   * @param {string} [overrideUrl] — optional URL to use instead of `src`
   */
  async open(overrideUrl) {
    if (overrideUrl) this.src = overrideUrl;
    await this._handleClick(new MouseEvent('click'));
  }
}

customElements.define('section-modal-trigger', SectionModalTrigger);




function getCartLineAttribute(attributes, key) {
  const found = (attributes || []).find((attr) => attr.key === key);
  return found ? found.value : null;
}

// 将 defaultVariantId（可能是 GID 字符串或数字）统一解析为 number|null
function resolveVariantId(raw) {
  if (!raw) return null;
  if (typeof raw === 'string' && raw.startsWith('gid://')) return Number(raw.split('/').pop()) || null;
  return Number(raw) || null;
}

async function getCartCurrentVariantWithGifts(currentVariantId) {
  const cartToken = _getCartToken();
  if (!currentVariantId || !cartToken) return null;

  const cartId = `gid://shopify/Cart/${cartToken}`;
  const query = `query GetCurrentBggCartLines($cartId: ID!) {
      cart(id: $cartId) {
        lines(first: 250) {
          nodes {
            id
            viewKey
            quantity
            merchandise {
              ... on ProductVariant {
                id
                title
                product { handle }
              }
            }
            attributes { key value }
            ... on CartLine {
              parentRelationship {
                parent { id viewKey }
              }
            }
          }
        }
      }
    }`;

  try {
    const response = await fetch('/api/2026-07/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables: { cartId } }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const json = await response.json();
    if (json.errors?.length) throw new Error(json.errors[0].message || 'Cart GraphQL failed');

    const lines = json?.data?.cart?.lines?.nodes ?? [];
    const parentLine = lines.find((line) => {
      if (getCartLineAttribute(line.attributes, '_b2g') === 'true') return false;
      return resolveVariantId(line.merchandise?.id) === Number(currentVariantId);
    });

    const existingGifts = parentLine
      ? lines
        .filter((line) => {
          if (getCartLineAttribute(line.attributes, '_b2g') !== 'true') return false;
          const parent = line.parentRelationship?.parent;
          return parent?.viewKey === parentLine.viewKey || parent?.id === parentLine.id;
        })
        .map((line) => ({
          lineId: line.id,
          viewKey: line.viewKey,
          variantId: resolveVariantId(line.merchandise?.id),
          variantGid: line.merchandise?.id || null,
          variantTitle: line.merchandise?.title || '',
          productHandle: line.merchandise?.product?.handle || '',
          quantity: line.quantity,
          giftId: getCartLineAttribute(line.attributes, '_b2g_gift_id'),
          activityId: getCartLineAttribute(line.attributes, '_b2g_activity_id'),
        }))
      : [];

    return {
      currentVariantId,
      parentLine: parentLine,
      existingGifts,
    };
  } catch (error) {
    console.error('[B2G] getCartCurrentVariantWithGifts failed', error);
    return null;
  }
}


function buildGiftVariantDiff({ parentLine, existingGifts, selectedGifts, giftsConfig, addQuantity }) {
  if (!parentLine) return null;

  const selected = selectedGifts ?? [];
  if (selected.length === 0) return null;

  const parentLineId = parentLine.id;

  const newByGiftId = new Map(selected.map((s) => [String(s.giftId), s]));
  const existingByGiftId = new Map((existingGifts ?? []).map((g) => [String(g.giftId), g]));

  // ── 循环一：构造工作结构 ──────────────────────────────────────────────────
  // 按 giftsConfig 顺序合并三个数据源，得到每个 giftId 的完整状态。
  /** @type {Array<{
   *   giftId: string, defaultVariantId: string, perGrantQty: number,
   *   productHandle: string, remainingQty: number|null,
   *   lineId: string|null, viewKey: string|null,
   *   variantId: number|null, variantGid: string|null, quantity: number,
   *   newVariantId: number|null, activityId: string|null,
   * }>} */
  const workItems = [];
  for (const config of (giftsConfig ?? [])) {
    const giftId = String(config.giftId);
    const existing = existingByGiftId.get(giftId) ?? null;
    const newSel = newByGiftId.get(giftId) ?? null;

    workItems.push({
      // 来自 giftsConfig
      giftId,
      defaultVariantId: config.defaultVariantId ?? null,
      perGrantQty: Number(config.perGrantQty) || 1,
      productHandle: config.productHandle ?? null,
      remainingQty: config.remainingQty != null ? Number(config.remainingQty) : null,
      // 来自 existingGifts（购物车内已有赠品行）
      lineId: existing?.lineId ?? null,
      viewKey: existing?.viewKey ?? null,
      variantId: existing ? Number(existing.variantId) : null,
      variantGid: existing?.variantGid ?? null,
      quantity: existing ? Number(existing.quantity) : 0,
      activityId: existing?.activityId ?? newSel?.activityId ?? null,
      // 来自 selectedGifts（本次用户选择的新变体及数量）
      newVariantId: newSel ? Number(newSel.variantId) : null,
      newQty: newSel ? (Number(newSel.qty) || 1) : 0,
      // 最终数量（含旧行 + 新增），循环二计算后填充
      finalQty: null,  // 循环二计算出的最终数量（含旧行 + 新增）
    });
  }

  // ── 循环二：按顺序分配配额，计算 finalQty 和 selectedTotal ─────────────────
  let quota = parentLine.quantity + (addQuantity || 0);
  let selectedTotal = 0;

  for (const item of workItems) {
    if (item.newVariantId == null) {
      // 不在本次选择中，保持现状
      item.finalQty = item.quantity;
      selectedTotal += item.quantity;
      continue;
    }
    if (item.remainingQty !== null && item.remainingQty <= 0) {
      // 库存耗尽，该赠品不分配（旧行后续会被删除）
      item.finalQty = 0;
      continue;
    }

    // newQty 由调用方保证，直接作为本 giftId 的期望新增量；
    // 受 remainingQty 约束后取实际可分配量
    const desiredQty = item.newQty + item.quantity;
    const qty = item.remainingQty !== null
      ? Math.min(desiredQty, item.remainingQty)
      : desiredQty;
    item.finalQty = qty;
    selectedTotal += qty;
  }

  // 选中总数与主商品数量不符（库存不足导致缺口），按 giftsConfig 顺序将剩余配额
  // 逐一补到尚有库存的 giftId，直到 selectedTotal === quota 或无可补赠品为止
  if (selectedTotal < quota) {
    let gap = quota - selectedTotal;
    for (const item of workItems) {
      if (gap <= 0) break;
      const remaining = item.remainingQty != null ? Number(item.remainingQty) : null;
      if (remaining !== null && remaining <= item.finalQty) continue; // 该 giftId 库存已满
      const canAdd = remaining !== null ? remaining - item.finalQty : gap;
      const fill = Math.min(gap, canAdd);
      item.finalQty += fill;
      selectedTotal += fill;
      gap -= fill;
      // 确保循环三能走"新增"分支：用 defaultVariantId 补齐 newVariantId
      if (item.newVariantId == null) {
        item.newVariantId = resolveVariantId(item.defaultVariantId);
      }
    }
  }

  // 选中总数超出配额，从后往前削减超出部分
  if (selectedTotal > quota) {
    let excess = selectedTotal - quota;
    for (let i = workItems.length - 1; i >= 0; i--) {
      if (excess <= 0) break;
      const item = workItems[i];
      if (item.finalQty <= 0) continue;
      const cut = Math.min(excess, item.finalQty);
      item.finalQty -= cut;
      selectedTotal -= cut;
      excess -= cut;
    }
  }

  // ── 循环三：根据 finalQty 生成操作列表 ───────────────────────────────────
  // remove: { id }
  // update: { id, quantity }
  // add:    { merchandiseId, quantity, parent, attributes }
  const ops = [];

  for (const item of workItems) {
    if (item.newVariantId == null) {
      // 不在本次选择中，但旧行可能因配额削减而数量变化
      if (item.variantId === null) continue; // 本就没有旧行，跳过
      if (item.finalQty === item.quantity) continue; // 数量未变，跳过
      if (item.finalQty > 0) {
        ops.push({ id: item.lineId, quantity: item.finalQty });
      } else {
        ops.push({ id: item.lineId, quantity: 0 });
      }
      continue;
    }

    if (item.variantId !== null && item.variantId !== item.newVariantId) {
      // 旧行存在且变体不同：删旧加新
      ops.push({ id: item.lineId, quantity: 0 });
      if (item.finalQty > 0) {
        ops.push({
          merchandiseId: `gid://shopify/ProductVariant/${item.newVariantId}`,
          quantity: item.finalQty,
          parent: { lineId: parentLineId },
          attributes: [
            { key: '_b2g', value: 'true' },
            { key: '_b2g_activity_id', value: String(item.activityId) },
            { key: '_b2g_gift_id', value: item.giftId },
          ],
        });
      }
    } else if (item.variantId === null && item.finalQty > 0) {
      // 无旧行，直接新增
      ops.push({
        merchandiseId: `gid://shopify/ProductVariant/${item.newVariantId}`,
        quantity: item.finalQty,
        parent: { lineId: parentLineId },
        attributes: [
          { key: '_b2g', value: 'true' },
          { key: '_b2g_activity_id', value: String(item.activityId) },
          { key: '_b2g_gift_id', value: item.giftId },
        ],
      });
    } else if (item.variantId !== null && item.variantId === item.newVariantId && item.finalQty !== item.quantity) {
      // 旧行变体相同但数量不一致：更新数量
      ops.push({ id: item.lineId, quantity: item.finalQty > 0 ? item.finalQty : 0 });
    }
  }

  if (ops.length === 0 && !addQuantity) return null;
  return [
    {
      id: parentLine.id,
      quantity: parentLine.quantity + addQuantity,
    },
    ...ops,
  ]
}



class BuyGetGift extends HTMLElement {
  constructor() {
    super();
    this._selections = new Map(); // giftId(string) → { variantId: number|null, qty: number, perQty: number, active: boolean }
    this._maxSelectable = 1;
    this._pdpQty = 1;
    this._fixedQty = true;
    this._gifts = [];
    this._i18n = {};
    this._qtyInputAbortController = null;
  }

  connectedCallback() {
    const dataEl = this.querySelector('script[type="application/json"]');
    if (!dataEl) return;

    let config;
    try {
      config = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    this._activityId = config.activityId || '';
    this._gifts = config.gifts || [];
    this._fixedQty = config.fixedQty === true;
    this._i18n = config.i18n || {};

    if (this._gifts.length === 0) return;

    this._pdpQty = this._readPdpQty();
    this._updateMaxSelectable();
    this._initDefaultSelections();

    this._bindCardEvents();
    this._updateUI();
    this._bindPdpQtyListener();
    this._bindVariantSelectedListener();
    this._bindVariantSwitcherModal();
    this._bindMainProductVariantListener();
    // this._shopifyPayBtnListener()
    this._bindBfcacheRestore();
  }

  // 将默认选择初始化抽离，方便 BFCache 恢复时单独调用
  _initDefaultSelections() {
    this._selections = new Map();
    const firstGift = this._gifts[0];
    if (firstGift) {
      const perQty = Math.max(1, firstGift.perGrantQty || 1);
      const id = String(firstGift.giftId);
      const variantId = resolveVariantId(firstGift.defaultVariantId);
      this._selections.set(id, { variantId, qty: perQty, perQty, active: true });
    }
  }

  disconnectedCallback() {
    if (this._qtyInputAbortController) {
      this._qtyInputAbortController.abort();
    }
    if (this._variantSelectedHandler) {
      document.removeEventListener('b2g:variant-selected', this._variantSelectedHandler);
    }
    if (this._bfcacheHandler) {
      window.removeEventListener('pageshow', this._bfcacheHandler);
    }
  }

  _bindBfcacheRestore() {
    this._bfcacheHandler = (e) => {
      this._restorePageState(e.persisted === false);
    };
    window.addEventListener('pageshow', this._bfcacheHandler);
  }

  _restorePageState(forceResetSelections = false) {
    this._incompleteShown = false;

    const shouldResetSelections = forceResetSelections || this._gifts.length === 0 || this._selections.size === 0;
    if (shouldResetSelections) {
      const dataEl = this.querySelector('script[type="application/json"]');
      if (dataEl) {
        try {
          const config = JSON.parse(dataEl.textContent);
          this._activityId = config.activityId || '';
          this._gifts = config.gifts || [];
          this._fixedQty = config.fixedQty === true;
          this._i18n = config.i18n || {};
        } catch (_) { }
      }
    }

    if (this._gifts.length === 0) return;

    this._pdpQty = this._readPdpQty();
    this._updateMaxSelectable();

    if (shouldResetSelections) {
      this._initDefaultSelections();
    } else {
      this._clampSelections();
    }

    this._updateUI();
  }

  _shopifyPayBtnListener() {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    const submitBtn = form.querySelector('.shopify-payment-button');
    submitBtn.addEventListener('click', (e) => {
      if (this.checkBeforeSubmit()) {
        e.preventDefault();
        return
      }
      this._dispatchChange()
    })
  }

  _readPdpQty() {
    const stepper = document.querySelector('pdp-qty-stepper [data-pdp-qty-input]');
    if (stepper) return Math.max(1, stepper.value || 1);
    const input = document.querySelector('[data-quantity-wrapper] [data-quantity-value]');
    const val = input ? parseInt(input.value, 10) : 1;
    return isNaN(val) || val < 1 ? 1 : val;
  }

  _readCurrentProductVariantId() {
    const form = document.querySelector('form[action*="/cart/add"]');
    const input = form && form.querySelector('[name="id"]');
    return resolveVariantId(input?.value);
  }

  _isSingleSelect() {
    return this._maxSelectable === 1 || this._gifts.length <= 1;
  }

  _updateMaxSelectable() {
    const base = this._fixedQty ? 1 : Math.max(1, this._pdpQty);
    // 受所有赠品库存总份数约束：有库存限制的赠品各自折算份数后求和
    const stockPortions = this._gifts.reduce((sum, gift) => {
      if (gift.remainingQty == null) return sum + base;
      const perQty = Math.max(1, gift.perGrantQty || 1);
      return sum + Math.floor(Number(gift.remainingQty) / perQty);
    }, 0);
    this._maxSelectable = Math.min(base, stockPortions);
    if (this._maxSelectable < 1) this._maxSelectable = 1;
  }

  _updateCardVariant(card, { variantId, variantTitle, variantImage, variantPrice }) {
    const textEl = card.querySelector('.bgg-card__variant-text');
    if (textEl && variantTitle) textEl.textContent = variantTitle;
    const sectionTrigger = card.querySelector('section-modal-trigger');
    if (sectionTrigger && variantId) {
      // 更新 variant，保留已有的 qty 参数
      const params = new URLSearchParams(sectionTrigger.getAttribute('data-params') || '');
      params.set('variant', String(variantId));
      sectionTrigger.setAttribute('data-params', params.toString());
    }
    const imgEl = card.querySelector('.bgg-card__image');
    if (imgEl && variantImage) imgEl.src = variantImage;
    const origPriceEl = card.querySelector('.bgg-card__original-price');
    if (origPriceEl && variantPrice != null) {
      if (variantPrice > 0) {
        const fmt =
          window.T4SThemeSP && window.T4SThemeSP.Currency
            ? window.T4SThemeSP.Currency.formatMoney(variantPrice)
            : (variantPrice / 100).toFixed(2);
        origPriceEl.textContent = fmt;
        origPriceEl.style.display = '';
      } else {
        origPriceEl.textContent = '';
        origPriceEl.style.display = 'none';
      }
    }
  }

  _bindVariantSwitcherModal() {
    /** 记录当前正在切换规格的 giftId（由 section-modal:before-fetch 设置） */
    this._pendingSwitchGiftId = null;

    // 监听触发元素的 before-fetch：记录 giftId，并将 stepper 当前数量同步到 data-params
    this.addEventListener('section-modal:before-fetch', (e) => {
      const trigger = e.target;
      const card = trigger.closest ? trigger.closest('.bgg-card') : null;
      this._pendingSwitchGiftId = card ? card.dataset.giftId : null;

      // 将 bgg-stepper 的实时数量写入 data-params，确保服务端渲染的 qty 正确
      if (trigger && card) {
        const stepperEl = card.querySelector('bgg-stepper');
        const currentQty = stepperEl ? (parseInt(stepperEl.dataset.value, 10) || 1) : 1;
        try {
          const params = new URLSearchParams(trigger.getAttribute('data-params') || '');
          params.set('qty', String(currentQty));
          trigger.setAttribute('data-params', params.toString());
        } catch (_) { }
      }
    });

    // 监听 switch-specs-modal 的确认事件，dispatch b2g:variant-selected
    const overlay = document.getElementById('switch-specs-modal-overlay');
    if (!overlay) return;

    overlay.addEventListener('dsm:dialog:confirm', () => {
      const body = overlay.querySelector('.switch-specs__body');

      // ── PDP mode only — cart mode is handled by BggCartModal ──
      const switcher = body && body.querySelector('variant-switcher');
      const variant = switcher && switcher.currentVariant;
      if (!variant) return;

      const giftId = this._pendingSwitchGiftId;
      if (!giftId) return;

      document.dispatchEvent(
        new CustomEvent('b2g:variant-selected', {
          bubbles: true,
          detail: {
            giftId,
            variantId: variant.id,
            variantTitle: variant.title,
            variantImage: variant.featured_image ? variant.featured_image.src : '',
            variantPrice: variant.price,
          },
        })
      );

      if (window.DsmDialog) window.DsmDialog.close('switch-specs-modal');
      this._pendingSwitchGiftId = null;
    });
  }

  _bindVariantSelectedListener() {
    this._variantSelectedHandler = (e) => {
      const { giftId, variantId, variantTitle, variantImage, variantPrice } = e.detail || {};
      if (!giftId || !variantId) return;
      // 更新 _gifts 中的 defaultVariantId
      const gift = this._gifts.find((g) => String(g.giftId) === String(giftId));
      if (gift) {
        const resolved = resolveVariantId(variantId);
        gift.defaultVariantId = resolved;
        // 同步更新 _selections 中的 variantId
        const sel = this._selections.get(String(giftId));
        if (sel) sel.variantId = resolved;
      }
      // 更新卡片 DOM
      const card = this.querySelector(`.bgg-card[data-gift-id="${CSS.escape(String(giftId))}"]`);
      if (card) this._updateCardVariant(card, { variantId, variantTitle, variantImage, variantPrice });
    };
    // quickshop 弹窗挂在 body 下，不在本元素子树内，需在 document 层监听
    document.addEventListener('b2g:variant-selected', this._variantSelectedHandler);
  }

  _bindPdpQtyListener() {
    if (this._fixedQty) return;

    // PdpQtyStepper dispatches 'qty:change' on plus/minus click and input change
    const stepper = document.querySelector('pdp-qty-stepper');
    const target = stepper;


    if (stepper) {
      this._qtyInputAbortController = new AbortController();
      const signal = this._qtyInputAbortController.signal;
      stepper.addEventListener(
        'qty:change',
        (e) => {
          const val = e.detail?.value;
          if (typeof val === 'number' && val >= 1) {
            const prev = this._pdpQty;
            this._pdpQty = val;
            this._updateMaxSelectable();
            this._clampSelections();
            if (val > prev) this._fillSelections();
            this._updateUI();
          }
        },
        { signal }
      );
    } else {
      const $ = window.jQuery_T4NT || window.jQuery
      const that = this;
      $("body").on("change", "[data-quantity-value]", function () {
        const val = $(this).val();
        if (!isNaN(val) && val >= 1) {
          const prev = that._pdpQty;
          that._pdpQty = val;
          that._updateMaxSelectable();
          that._clampSelections();
          if (val > prev) that._fillSelections();
          that._updateUI();
          // that._dispatchChange();
        }
      });

    }
  }

  _bindMainProductVariantListener() {
    const form = document.querySelector('form[action*="/cart/add"]');
    console.log('🚀 ~ form:', form)
    if (!form) return;
    // 变体切换后主题 JS 会重置 data-quantity-value 的 max 并可能修改 value，需同步赠品上限
    const syncQtyFromInput = (qty) => {

      if (isNaN(qty) || qty < 1) return;
      const prev = this._pdpQty;
      this._pdpQty = qty;
      this._updateMaxSelectable();
      this._clampSelections();
      if (qty > prev) this._fillSelections();
      this._updateUI();
    };
    // 变体切换：form[name=id] 变化
    form.addEventListener('change', (e) => {
      const input = form.querySelector('[data-quantity-value]');
      let qty;
      if (input) qty = parseInt(input.value, 10);
      if (!qty) return;

      syncQtyFromInput(qty);
    });
    // 兼容主题通过 CustomEvent 广播变体变化的场景
    form.addEventListener('variant:changed', (e) => {
      const qty = e.quantity || 1;
      syncQtyFromInput(qty);
    });
  }

  _clampSelections() {
    let total = this._totalSelected();
    if (total <= this._maxSelectable) return;

    for (const [, entry] of this._selections) {
      if (!entry.active) continue;
      if (total <= this._maxSelectable) break;
      const excess = total - this._maxSelectable; // 超出的份数
      const reducePortions = Math.min(entry.qty / entry.perQty, excess);
      entry.qty -= reducePortions * entry.perQty;
      total -= reducePortions;
      // 只有 qty 降到 0 时才取消选中
      if (entry.qty <= 0) {
        entry.qty = entry.perQty;
        entry.active = false;
        total -= 0; // active=false 后 _totalSelected 不再计入，无需再减
      }
    }
  }

  // 主商品数量增加时，从第一个礼品开始依次填满，第一个到上限后才补给第二个
  _fillSelections() {
    let remaining = this._maxSelectable - this._totalSelected();
    if (remaining <= 0) return;

    for (const gift of this._gifts) {
      if (remaining <= 0) break;
      const giftId = String(gift.giftId);
      const perQty = Math.max(1, gift.perGrantQty || 1);
      const maxForGift =
        gift.remainingQty != null
          ? Math.min(this._maxSelectable * perQty, Math.floor(Number(gift.remainingQty) / perQty) * perQty)
          : this._maxSelectable * perQty;

      let entry = this._selections.get(giftId);
      if (!entry) {
        entry = { variantId: resolveVariantId(gift.defaultVariantId), qty: 0, perQty, active: false };
        this._selections.set(giftId, entry);
      }

      if (!entry.active) {
        entry.active = true;
        entry.qty = perQty;
        remaining -= 1;
      }

      // 继续追加直到该礼品填满上限
      const canAdd = Math.min(Math.floor(remaining) * perQty, maxForGift - entry.qty);
      if (canAdd > 0) {
        entry.qty += canAdd;
        remaining -= canAdd / perQty;
      }
    }
  }

  // 返回已选总份数（每份 = perQty 件）
  _totalSelected() {
    let total = 0;
    for (const entry of this._selections.values()) {
      if (!entry.active) continue;
      total += entry.qty / entry.perQty;
    }
    return total;
  }

  _toggleGift(giftId) {
    const entry = this._selections.get(giftId);
    if (entry && entry.active) {
      // 单选模式下已选中的项不允许取消勾选
      if (this._isSingleSelect()) return;
      // 取消选中：qty 原样保留，仅将 active 置为 false
      entry.active = false;
    } else {
      if (this._isSingleSelect() && this._totalSelected() >= this._maxSelectable) {
        // 单选：把其他激活项全部关掉
        for (const e of this._selections.values()) e.active = false;
      } else if (this._totalSelected() >= this._maxSelectable) {
        return;
      }
      const gift = this._gifts.find((g) => g.giftId == giftId);
      const perQty = Math.max(1, gift ? gift.perGrantQty || 1 : 1);
      // 库存不足时拒绝选中
      if (gift && gift.remainingQty != null && Number(gift.remainingQty) < perQty) return;
      if (!entry) {
        // 首次选中：新建记录，qty 受配额与库存双重钳制
        const maxForGift = gift && gift.remainingQty != null
          ? Math.min(this._maxSelectable * perQty, Math.floor(Number(gift.remainingQty) / perQty) * perQty)
          : this._maxSelectable * perQty;
        const remainingPortions = this._maxSelectable - this._totalSelected();
        const initQty = Math.min(perQty, maxForGift, remainingPortions * perQty);
        this._selections.set(giftId, { variantId: resolveVariantId(gift?.defaultVariantId), qty: initQty, perQty, active: true });
      } else {
        // 恢复选中：qty 保留，但需钳制到当前可用配额
        const maxForGift =
          gift && gift.remainingQty != null
            ? Math.min(this._maxSelectable * perQty, Math.floor(Number(gift.remainingQty) / perQty) * perQty)
            : this._maxSelectable * perQty;
        const remainingPortions = this._maxSelectable - this._totalSelected();
        const maxByQuota = remainingPortions * perQty;
        entry.qty = Math.max(perQty, Math.min(entry.qty, maxForGift, maxByQuota));
        entry.active = true;
      }
    }
    this._updateUI();
  }

  _setQty(giftId, qty) {
    const entry = this._selections.get(giftId);
    if (!entry || !entry.active) return;
    const gift = this._gifts.find((g) => g.giftId == giftId);
    const perQty = entry.perQty;
    // maxForGift：单个礼品的件数上限 = 全局份数上限 × 每份件数，再受 remainingQty 约束
    const maxForGift =
      gift && gift.remainingQty != null
        ? Math.min(this._maxSelectable * perQty, Math.floor(Number(gift.remainingQty) / perQty) * perQty)
        : this._maxSelectable * perQty;
    const clamped = Math.max(perQty, Math.min(maxForGift, qty));
    // 通过份数差值检查是否超过全局上限
    const newTotal = this._totalSelected() - entry.qty / perQty + clamped / perQty;
    if (newTotal > this._maxSelectable) return;
    entry.qty = clamped;
    this._updateUI();
  }

  _incQty(giftId) {
    const entry = this._selections.get(giftId);
    if (!entry || !entry.active) return;
    if (this._totalSelected() >= this._maxSelectable) return;
    const gift = this._gifts.find((g) => g.giftId == giftId);
    const perQty = entry.perQty;
    // maxForGift：件数上限 = 全局份数上限 × 每份件数，再受 remainingQty 约束
    const maxForGift =
      gift && gift.remainingQty != null
        ? Math.min(this._maxSelectable * perQty, Math.floor(Number(gift.remainingQty) / perQty) * perQty)
        : this._maxSelectable * perQty;
    const next = entry.qty + perQty;
    if (next <= maxForGift) {
      entry.qty = next;
      this._updateUI();
    }
  }

  _decQty(giftId) {
    const entry = this._selections.get(giftId);
    if (!entry || !entry.active) return;
    const perQty = entry.perQty;
    const next = entry.qty - perQty;
    if (next < perQty) {
      entry.active = false;
      entry.qty = perQty; // 重置为 perQty，下次重新选中从最小值开始
    } else {
      entry.qty = next;
    }
    this._updateUI();
  }

  _dispatchChange() {
    const selections = [];
    for (const [giftId, entry] of this._selections) {
      if (!entry.active) continue;
      const gift = this._gifts.find((g) => g.giftId == giftId);
      if (gift) {
        selections.push({
          giftId: gift.giftId,
          variantId: entry.variantId,
          productHandle: gift.productHandle,
          qty: entry.qty,
        });
      }
    }
    this._syncFormInputs(selections);
  }

  /**
   * 将当前已选赠品同步为 product form 中的隐藏字段。
   * 遵循 Shopify accelerated checkout 多商品规范：
   *   shopify-additional-items[i][id]          — variant ID
   *   shopify-additional-items[i][quantity]    — 数量
   *   shopify-additional-items[i][parent_id]   — 固定为 1（主商品在同一表单中的 index）
   *   shopify-additional-items[i][properties][_b2g]             — 标记
   *   shopify-additional-items[i][properties][_b2g_activity_id] — 活动 ID
   *   shopify-additional-items[i][properties][_b2g_gift_id]     — 礼品 ID
   *
   * @param {Array<{giftId, variantId, qty}>} selections
   */
  _syncFormInputs(selections) {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    // 清理上一次写入的隐藏字段
    form.querySelectorAll('[data-bgg-field]').forEach((el) => el.remove());

    const parentIdInput = form.querySelector('[name="id"]');
    const parentId = parentIdInput ? parentIdInput.value : '1';

    selections.forEach((sel, idx) => {
      if (!sel.variantId) return;
      const i = idx + 1; // index 从 1 开始（主商品占 index 0）

      const fields = [
        [`shopify-additional-items[${i}][id]`, String(sel.variantId)],
        [`shopify-additional-items[${i}][quantity]`, String(sel.qty)],
        [`shopify-additional-items[${i}][parent_id]`, '1'],
        [`shopify-additional-items[${i}][properties]`, JSON.stringify({
          _b2g: 'true',
          _b2g_activity_id: String(this._activityId),
          _b2g_gift_id: String(sel.giftId),
        })],
      ];

      fields.forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.dataset.bggField = '1';
        form.appendChild(input);
      });
    });


  }

  // ── Public cart API ──────────────────────────────────────────────────────

  /**
   * 返回当前已选赠品的 /cart/add.js items 数组。
   *
   * @param {object}  [opts]
   * @param {number}  [opts.parentId]       同一 add 请求中主商品的 1-based index（用于 parent_id）
   * @param {string}  [opts.parentLineKey]  购物车中已存在的主商品 line key（用于 parent_line_key）
   * @returns {Array<object>}  可直接拼入 items[] 的赠品条目
   *
   * 用法（与主商品同请求加购）：
   *   const giftItems = bgg.getGiftCartItems({ parentId: 1 });
   *   // items = [mainItem, ...giftItems]
   *
   * 用法（独立追加赠品到已有购物车）：
   *   const giftItems = bgg.getGiftCartItems({ parentLineKey: existingLineKey });
   */
  getGiftCartItems({ parentId, parentLineKey } = {}) {
    const items = [];
    for (const [giftId, entry] of this._selections) {
      if (!entry.active) continue;
      const variantId = entry.variantId;
      if (!variantId) continue;
      const gift = this._gifts.find((g) => g.giftId == giftId);
      if (!gift) continue;
      const item = {
        id: variantId,
        quantity: entry.qty,
        properties: {
          _b2g: 'true',
          _b2g_activity_id: String(this._activityId),
          _b2g_gift_id: String(gift.giftId),
        },
        remainingQty: gift.remainingQty ?? null,
      };
      if (parentId != null) item.parent_id = parentId;
      else if (parentLineKey) item.parent_line_key = parentLineKey;
      items.push(item);
    }
    return items;
  }

  /**
   * 如果礼品未全部选满则弹出提示弹窗，返回 true 表示已拦截（未选满），false 表示已选满可继续。
   * 供外部 add-to-cart 逻辑调用，例：
   *   const bgg = document.querySelector('buy-get-gift');
   *   if (bgg && bgg.checkBeforeSubmit()) return; // aborted
   */
  checkBeforeSubmit() {
    // this._dispatchChange();
    if (this._incompleteShown) return false;
    const remaining = this._maxSelectable - this._totalSelected();
    if (remaining <= 0) return false;

    // 计算所有赠品的库存总份数上限；若库存不足导致无法选满，则静默放行
    const stockPortions = this._gifts.reduce((sum, gift) => {
      if (gift.remainingQty == null) return sum + this._maxSelectable; // 无库存限制视为充足
      const perQty = Math.max(1, gift.perGrantQty || 1);
      return sum + Math.floor(Number(gift.remainingQty) / perQty);
    }, 0);
    if (stockPortions < this._maxSelectable) return false;

    this._incompleteShown = true;
    this._showIncompleteModal(remaining);
    return true;
  }

  _showIncompleteModal(remaining) {
    const i18n = this._i18n || {};
    const title = i18n.incompleteTitle;
    const bodyTemplate = (i18n.incompleteBody || '').replace('__remaining__', String(remaining));
    const confirmLabel = i18n.incompleteConfirm;

    const overlayEl = this.querySelector('#bgg-incomplete-modal-overlay');
    if (!overlayEl) return;

    // 更新弹窗标题
    const titleEl = overlayEl.querySelector('.dsm-modal__title');
    if (titleEl) titleEl.textContent = title;

    // 更新弹窗正文
    const bodyEl = overlayEl.querySelector('#bgg-incomplete-modal-desc');
    if (bodyEl) bodyEl.textContent = bodyTemplate;

    // 更新确认按钮文字
    const confirmBtn = overlayEl.querySelector('.js-dsm-dialog-confirm');
    if (confirmBtn) {
      const labelEl = confirmBtn.querySelector('[class*="dsm-button__label"], .dsm-btn__label, span');
      if (labelEl) labelEl.textContent = confirmLabel;
      else confirmBtn.textContent = confirmLabel;
    }

    // 点击确认按钮关闭弹窗，并滚动到礼品区
    if (confirmBtn && !confirmBtn.dataset.bggBound) {
      confirmBtn.dataset.bggBound = '1';
      confirmBtn.addEventListener('click', () => {
        if (window.DsmDialog) window.DsmDialog.close('bgg-incomplete-modal');
        const bggEl = this.querySelector('.bgg');
        if (bggEl) bggEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    if (window.DsmDialog) {
      window.DsmDialog.open('bgg-incomplete-modal');
    }
  }

  _bindCardEvents() {
    this.querySelectorAll('.bgg-card').forEach((card) => {
      const giftId = card.dataset.giftId;

      card.addEventListener('click', (e) => {
        if (e.target.closest('bgg-stepper')) return;
        if (e.target.closest('section-modal-trigger')) return;
        this._toggleGift(giftId);
      });

      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (e.target.closest('bgg-stepper')) return;
          if (e.target.closest('section-modal-trigger')) return;
          this._toggleGift(giftId);
        }
      });

      const stepper = card.querySelector('bgg-stepper');
      if (stepper) {
        stepper.addEventListener('bgg-stepper:dec', (e) => {
          e.stopPropagation();
          this._decQty(giftId);
        });
        stepper.addEventListener('bgg-stepper:inc', (e) => {
          e.stopPropagation();
          this._incQty(giftId);
        });
        stepper.addEventListener('bgg-stepper:set', (e) => {
          e.stopPropagation();
          this._setQty(giftId, e.detail.value);
        });
      }
    });
  }

  _updateUI() {
    const total = this._totalSelected();
    const isFull = total >= this._maxSelectable;
    const singleSelect = this._isSingleSelect();

    // 同步容器的单/多选 class，CSS 用来切换 checkbox 形状
    const bggEl = this.querySelector('.bgg');
    if (bggEl) bggEl.classList.toggle('bgg--single-select', singleSelect);

    const countEl = this.querySelector('.bgg__count');
    if (countEl) {
      if (singleSelect) {
        countEl.textContent = '';
      } else {
        const pattern = this._i18n && this._i18n.selectedCount;
        countEl.textContent = pattern.replace('__selected__', total).replace('__total__', this._maxSelectable);
      }
    }

    this.querySelectorAll('.bgg-card').forEach((card) => {
      const giftId = card.dataset.giftId;
      const gift = this._gifts.find((g) => g.giftId == giftId);
      const entry = this._selections.get(giftId);
      const isSelected = entry ? entry.active : false;
      const qty = entry ? entry.qty : 0;
      const isDisabled = !isSelected && isFull && !singleSelect;

      card.classList.toggle('bgg-card--selected', isSelected);
      card.classList.toggle('bgg-card--disabled', isDisabled);
      card.setAttribute('aria-pressed', String(isSelected));
      card.setAttribute('tabindex', isDisabled ? '-1' : '0');

      const stepperEl = card.querySelector('bgg-stepper');
      if (stepperEl) {
        const perQty = entry ? entry.perQty : Math.max(1, gift ? gift.perGrantQty || 1 : 1);
        // maxForGift：件数上限 = 全局份数上限 × 每份件数，再受 remainingQty 约束
        let maxForGift =
          gift && gift.remainingQty != null
            ? Math.min(this._maxSelectable * perQty, Math.floor(Number(gift.remainingQty) / perQty) * perQty)
            : this._maxSelectable * perQty;
        // 全局份数已满且当前礼品没有剩余额度可增加 → 将 max 钳制为当前 qty，使 inc 按钮 disabled
        if (isFull && isSelected) {
          maxForGift = Math.min(maxForGift, qty);
        }
        stepperEl.dataset.value = String(qty > 0 ? qty : perQty);
        stepperEl.dataset.min = String(perQty);
        stepperEl.dataset.max = String(maxForGift);
        stepperEl.dataset.disabled = String(!isSelected);
      }
    });
  }

  getSelections() {
    const selections = [];
    for (const [giftId, entry] of this._selections) {
      if (!entry.active) continue;
      const gift = this._gifts.find((g) => g.giftId == giftId);
      if (gift) {
        selections.push({
          giftId: gift.giftId,
          variantId: entry.variantId,
          productHandle: gift.productHandle,
          qty: entry.qty,
        });
      }
    }
    return selections;
  }

  getGifts(parentItem) {
    const selections = [];
    for (const [giftId, entry] of this._selections) {
      if (!entry.active) continue;
      const gift = this._gifts.find((g) => g.giftId == giftId);
      if (gift) {
        const giftItem = {
          merchandiseId: `gid://shopify/ProductVariant/${entry.variantId}`,
          quantity: entry.qty,
          parent: {
            merchandiseId: `gid://shopify/ProductVariant/${parentItem.id}`,
          },
          attributes: [
            { key: '_b2g', value: 'true' },
            { key: '_b2g_activity_id', value: this._activityId },
            { key: '_b2g_gift_id', value: gift.giftId },
          ]
        }
        selections.push(giftItem);
      }
    }

    return selections;
  }

  _normalizeSubmitFormData(formData) {
    if (!formData) return {};
    if (typeof formData.entries === 'function') {
      const formObj = {};
      for (const [key, value] of formData.entries()) {
        formObj[key] = value;
      }
      return formObj;
    }
    return { ...formData };
  }

  getDefaultSubmitData(formData) {
    const formObj = this._normalizeSubmitFormData(formData);
    return [
      {
        merchandiseId: `gid://shopify/ProductVariant/${formObj.id}`,
        quantity: parseInt(formObj.quantity, 10) || 1,
      },
      ...this.getGifts({ id: formObj.id })
    ]
  }

  async onSubmit(that, data) {
    const { formData, clearLoading, isOld } = data;

    let onSuccess = data.onSuccess;
    let onError = data.onError;
    let onFetchError = data.onFetchError;

    if (isOld) {
      const jq = window.jQuery_T4NT || window.jQuery;
      const doc = jq(document);
      onSuccess = () => {
        doc.trigger("add:cart:success").trigger("add:cart:upsell");
        T4SThemeSP.isATCSuccess = true;
        T4SThemeSP.Cart.getToFetch();
      }

      onFetchError = () => {
        doc.trigger("cart:updated", ["error"]);
      }

      onError = () => {
        doc.trigger("cart:updated");
      }
    }

    if (this.checkBeforeSubmit()) {
      clearLoading && clearLoading.call(that);
      onError && onError.call(that);
      return;
    }

    const formObj = this._normalizeSubmitFormData(formData);
    const variantId = formObj.id;
    const addQuantity = parseInt(formObj.quantity, 10) || 1;
    try {
      const cartCurrentVariantWithGifts = await getCartCurrentVariantWithGifts(variantId);
      let ops = buildGiftVariantDiff({
        ...cartCurrentVariantWithGifts,
        giftsConfig: this._gifts,
        selectedGifts: this.getSelections(),
        addQuantity
      });

      if (!ops || ops.length === 0) {
        ops = this.getDefaultSubmitData(formData);
      }

      const data = await Shopify.actions.updateCart({
        lines: ops
      })

      if (data.status) {
        onError && onError.call(that, data);
        return;
      }
      onSuccess && onSuccess.call(that, data);
    } catch (error) {
      onFetchError && onFetchError.call(that, error);
      console.error('[B2G] Error fetching cart current variant with gifts:', error);
    }
  }

  _createBuyNowStorefrontCart(lines) {
    if (!lines.length) return Promise.resolve(null);

    const query = `mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            id
            checkoutUrl
          }
          userErrors {
            field
            message
          }
          warnings {
            message
          }
        }
      }`;

    return fetch('/api/2026-10/graphql.json', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        variables: {
          input: {
            lines,
          },
        },
      }),
    }).then((response) => {
      if (!response.ok) throw response;
      return response.json();
    }).then((result) => {
      const payload = result && result.data && result.data.cartCreate;
      const errors = (result && result.errors) || (payload && payload.userErrors) || [];

      if (errors.length) {
        throw new Error(errors.map((error) => error.message).filter(Boolean).join('; ') || 'Storefront cartCreate failed');
      }

      return payload && payload.cart && payload.cart.checkoutUrl ? payload.cart.checkoutUrl : null;
    });
  }

  onBuyNow(item, data = {}) {
    const { clearLoading } = data;
    if (this.checkBeforeSubmit()) {
      clearLoading && clearLoading();
      return;
    }

    const lines = this.getBuyNowItems(item);
    this._createBuyNowStorefrontCart(lines)
      .then((checkoutUrl) => {
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        } else {
          console.error('[B2G] Failed to create Storefront cart for Buy Now');
        }
      })
      .finally(() => {
        clearLoading && clearLoading();
      })
      .catch((error) => {
        console.error('[B2G] Error creating Storefront cart for Buy Now:', error);
      });
  }

  getBuyNowItems(item) {
    return [
      {
        merchandiseId: `gid://shopify/ProductVariant/${item.id}`,
        quantity: parseInt(item.quantity, 10) || 1
      },
      ...this.getGifts({ id: item.id })
    ]
  }
}

customElements.define('buy-get-gift', BuyGetGift);

// ── BggCartModal custom element ─────────────────────────────────────────
//
// Handles the cart-mode bgg-variant-modal: shows already-in-cart gift cards
// with inline variant-switchers and, on confirm, dispatches
// `b2g:cart-variant-selected` so the cart page can update line items.
//
// This element is intentionally thin — it owns zero selection state.
// All selection management is handled upstream by the cart page JS.
class BggCartModal extends HTMLElement {
  static variantMapCache = null;
  // giftId(string) → { variantId: number|null, qty: number, perQty: number, active: boolean }
  // perQty: 一份赠品对应的件数（= bgg-stepper[data-step]）
  // Mirrors BuyGetGift._selections; source of truth for confirm handler & hint bar.
  constructor() {
    super();
    this._selections = new Map();
  }

  connectedCallback() {
    this._ac = new AbortController();
    const { signal } = this._ac;

    // 父购物车行数量 = 可选份数上限（每份包含 perGrantQty 件礼品）
    this._maxSelect = Math.max(1, parseInt(this.dataset.maxSelect, 10) || 1);

    this.constructor.variantMapCache = this._getVariantData();
    this._activityId = this.constructor.variantMapCache?.activityId
    this.activity = this._getActivityData(this._activityId);

    // fixedQty: true → 份数固定为 1，与主商品购买数量无关（与 BuyGetGift._updateMaxSelectable 行为一致）
    const fixedQty = this.constructor.variantMapCache?.fixedQty === true;
    const rawMaxSelect = Math.max(1, parseInt(this.dataset.maxSelect, 10) || 1);
    this._maxSelect = fixedQty ? 1 : rawMaxSelect;
    this._initSelections();

    this._bindCardClicks(signal);
    this._bindVariantChange(signal);
    this._bindStepperEvents(signal);
    this._updateHintBar();
    this._updateAllSteppersUI();

    const overlay = document.getElementById('switch-specs-modal-overlay');
    if (!overlay) return;

    overlay._dsmOnConfirm = async () => {
      const varMap = this.constructor.variantMapCache;
      if (!varMap) return; // not a cart-mode modal

      const parentCartLineViewKey = varMap.parentCartLineViewKey;
      const currentLines = varMap.lines;

      // 1. 通过 SFAPI 查询各 CartLine 的真实 GID（built-in getCart 不含 viewKey）
      const parentLineGid = await this._resolveParentLineGid(parentCartLineViewKey);
      if (!parentLineGid) {
        console.warn('[B2G] Could not resolve parent CartLine GID for viewKey', parentCartLineViewKey);
        return;
      }

      const opsArr = [];

      // 2. 按 giftId 匹配现有礼品行，尽量 update 而非 delete+add，避免 CartLine 记录中断：
      //    - 已有行且选中 → update quantity（+ merchandiseId 切换规格）
      //    - 已有行但未选中 → remove（quantity: 0）
      //    - 无对应行但选中 → add（merchandiseId + parent.lineId）
      //    - 孤立的现有行（_selections 里没有对应 giftId）→ remove

      // 建立 giftId → currentLine 的索引
      const existingByGiftId = new Map();
      currentLines.forEach((line) => {
        if (line.giftId) existingByGiftId.set(String(line.giftId), line);
      });

      const handledViewKeys = new Set();

      for (const [giftId, sel] of this._selections) {
        const existing = existingByGiftId.get(giftId);
        if (sel.active) {
          if (existing) {
            // 更新现有行（规格 + 数量）
            opsArr.push({
              id: existing.viewKey,
              merchandiseId: `gid://shopify/ProductVariant/${sel.variantId}`,
              quantity: sel.qty,
            });
            handledViewKeys.add(existing.viewKey);
          } else {
            // 新增行
            opsArr.push({
              merchandiseId: `gid://shopify/ProductVariant/${sel.variantId}`,
              quantity: sel.qty,
              parent: { lineId: parentLineGid },
              attributes: [
                { key: '_b2g', value: 'true' },
                { key: '_b2g_activity_id', value: String(this._activityId) },
                { key: '_b2g_gift_id', value: String(giftId) }
              ]
            });
          }
        } else if (existing) {
          // 取消选中 → 删除；已标记 handled，避免孤立行 forEach 重复生成删除 op
          opsArr.push({ id: existing.viewKey, quantity: 0 });
          handledViewKeys.add(existing.viewKey);
        }
      }

      // 孤立行（_selections 里没有对应 giftId 的现有行）一律删除
      currentLines.forEach((line) => {
        if (line.viewKey && !handledViewKeys.has(line.viewKey)) {
          opsArr.push({ id: line.viewKey, quantity: 0 });
        }
      });

      if (opsArr.length === 0) return;

      document.dispatchEvent(
        new CustomEvent('b2g:cart-variant-selected', {
          bubbles: true,
          detail: { parentCartLineViewKey, ops: opsArr },
        })
      );

      await Shopify.actions.updateCart({
        lines: opsArr
      });

      // 触发主题 ajax 购物车刷新：找到主商品行对应的 input[data-quantity-value]，
      // 触发 change:ajaxCart 事件，让 interactable.js 重新渲染购物车 DOM。

      T4SThemeSP.Cart?.getToFetch()
    };
  }

  // Build _selections from the current DOM state (is-selected class + variant-switcher.currentVariant + stepper qty).
  // perQty 优先从 this.activity.gifts[].perGrantQty 获取，回退到 stepper[data-step]。
  _initSelections() {
    this._selections.clear();
    this.querySelectorAll('.bgg-cart-card').forEach((card) => {
      const giftId = String(card.dataset.giftId);
      const active = card.classList.contains('is-selected');
      const giftItem = this.activity.gifts.find((g) => g.giftId === giftId);
      const variantId = card.dataset.variantId
      const stepperEl = card.querySelector(`bgg-stepper[data-gift-id="${giftId}"]`);

      const perQty = giftItem.perGrantQty
      const qty = stepperEl ? Math.max(perQty, parseInt(stepperEl.dataset.value, 10) || perQty) : perQty;
      // domMax: Liquid 计算的初始上限（含库存约束），缓存起来避免后续被覆盖
      const domMax = stepperEl ? Math.max(perQty, parseInt(stepperEl.dataset.max, 10) || perQty) : perQty;
      this._selections.set(giftId, { variantId, qty, perQty, domMax, active });
    });
    console.log('🚀 ~ this._selections:', this._selections)
  }

  // 所有 active 选项的份数之和（qty / perQty，以 perQty 为份数单位）
  _totalPortions() {
    let total = 0;
    for (const sel of this._selections.values()) {
      if (!sel.active) continue;
      total += sel.qty / sel.perQty;
    }
    return total;
  }

  _updateHintBar() {
    const bar = this.querySelector('.bgg-cart-hint-bar');
    if (!bar) return;
    const tpl = bar.dataset.countTpl;
    if (!tpl) return;
    const portions = this._totalPortions();
    const textEl = bar.querySelector('.bgg-cart-hint-text');
    if (textEl) textEl.innerHTML = tpl.replace('__selected__', String(portions));
  }

  /**
   * 通过 SFAPI 查询购物车各行的 { id, viewKey }，按 viewKey 匹配返回主商品行的真实 CartLine GID。
   * @param {string} parentCartLineViewKey — Liquid line.key（格式 variantId:hash）
   * @returns {Promise<string|null>}
   */
  async _resolveParentLineGid(parentCartLineViewKey) {
    const cartToken = _getCartToken();
    if (!cartToken) return null;
    const cartId = `gid://shopify/Cart/${cartToken}`;
    try {
      const sfRes = await fetch('/api/2026-07/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `query GetCartLines($cartId: ID!) {
            cart(id: $cartId) {
              lines(first: 250) {
                nodes { id viewKey }
              }
            }
          }`,
          variables: { cartId },
        }),
      }).then((r) => r.json());
      const nodes = sfRes?.data?.cart?.lines?.nodes ?? [];
      return nodes.find((n) => n.viewKey === parentCartLineViewKey)?.id ?? null;
    } catch (e) {
      console.error('[B2G] SFAPI cart query failed', e);
      return null;
    }
  }

  _getVariantData() {
    const mapEl = this.querySelector('script[data-variant-switcher-variants]');
    if (!mapEl) return null;
    try {
      const map = JSON.parse(mapEl.textContent);
      return map;
    } catch (_) {
      return null;
    }
  }

  _getActivityData(activityId) {
    if (!activityId) return null;
    const configElement = document.getElementById('t4s-b2g-config');
    if (!configElement) return null;
    try {
      const config = JSON.parse(configElement.textContent);
      return config.activities.find((act) => act.id === activityId) || null;
    } catch (_) {
      return null;
    }
  }

  _bindCardClicks(signal) {
    this.addEventListener(
      'click',
      (e) => {
        // Ignore clicks inside variant-switcher (swatch) or stepper
        if (e.target.closest('variant-switcher')) return;
        if (e.target.closest('bgg-stepper')) return;

        const card = e.target.closest('.bgg-cart-card');
        if (!card) return;

        const giftId = String(card.dataset.giftId);
        const isSingle = card.closest('.bgg--single-select') !== null;
        const sel = this._selections.get(giftId);
        const isSelected = sel ? sel.active : false;

        if (isSingle) {
          if (!isSelected) {
            // Deactivate all, then activate this one
            for (const [id, s] of this._selections) s.active = id === giftId;
            this.querySelectorAll('.bgg-cart-card').forEach((c) =>
              c.classList.toggle('is-selected', String(c.dataset.giftId) === giftId)
            );
          }
        } else {
          const next = !isSelected;
          // 选中时检查总份数是否已满
          if (next && this._totalPortions() >= this._maxSelect) return;
          if (sel) sel.active = next;
          card.classList.toggle('is-selected', next);
        }

        this._updateHintBar();
        this._updateAllSteppersUI();
      },
      { signal }
    );
  }

  // Keep _selections.variantId in sync when a swatch is changed.
  _bindVariantChange(signal) {
    this.addEventListener(
      'variant-switcher:change',
      (e) => {
        const switcher = e.target.closest('variant-switcher');
        if (!switcher) return;
        const giftId = String(switcher.dataset.giftId);
        const sel = this._selections.get(giftId);
        if (sel) sel.variantId = Number(e.detail.variant.id) || null;
        // DOM sync (img + price) is handled by the bgg-variant-card WC wrapping each card
      },
      { signal }
    );
  }

  // Sync _selections.qty and stepper DOM on bgg-stepper:dec/inc/set events.
  _bindStepperEvents(signal) {
    this.addEventListener(
      'bgg-stepper:dec',
      (e) => {
        e.stopPropagation();
        const stepperEl = e.target.closest('bgg-stepper');
        if (!stepperEl) return;
        const giftId = String(stepperEl.dataset.giftId);
        const sel = this._selections.get(giftId);
        if (!sel || !sel.active) return;
        const step = sel.perQty;
        const next = sel.qty - step;
        if (next < step) {
          sel.active = false;
          sel.qty = step;
          const card = stepperEl.closest('.bgg-cart-card');
          if (card) card.classList.remove('is-selected');
        } else {
          sel.qty = next;
        }
        this._updateHintBar();
        this._updateAllSteppersUI();
      },
      { signal }
    );

    this.addEventListener(
      'bgg-stepper:inc',
      (e) => {
        e.stopPropagation();
        const stepperEl = e.target.closest('bgg-stepper');
        if (!stepperEl) return;
        const giftId = String(stepperEl.dataset.giftId);
        const sel = this._selections.get(giftId);
        if (!sel || !sel.active) return;
        const step = sel.perQty;
        // 检查加一份后是否超过总份数上限
        const portionsWithout = this._totalPortions() - sel.qty / step;
        if (portionsWithout + (sel.qty + step) / step > this._maxSelect) return;
        sel.qty = sel.qty + step;
        this._updateHintBar();
        this._updateAllSteppersUI();
      },
      { signal }
    );

    this.addEventListener(
      'bgg-stepper:set',
      (e) => {
        e.stopPropagation();
        const stepperEl = e.target.closest('bgg-stepper');
        if (!stepperEl) return;
        const giftId = String(stepperEl.dataset.giftId);
        const sel = this._selections.get(giftId);
        if (!sel || !sel.active) return;
        const step = sel.perQty;
        const maxQty = (this._maxSelect - this._totalPortions() + sel.qty / step) * step;
        const clamped = Math.max(step, Math.min(maxQty, e.detail.value));
        sel.qty = clamped;
        this._updateHintBar();
        this._updateAllSteppersUI();
      },
      { signal }
    );
  }

  // Reflect sel.qty back to the stepper's data-value.
  _updateStepperUI(giftId) {
    const sel = this._selections.get(giftId);
    if (!sel) return;
    const stepperEl = this.querySelector(`bgg-stepper[data-gift-id="${giftId}"]`);
    if (!stepperEl) return;
    stepperEl.dataset.value = String(sel.qty);
    stepperEl.dataset.disabled = String(!sel.active);
    // 更新 max：受总份数剩余约束（排除自身后的剩余份数 × perQty）
    const portionsWithout = this._totalPortions() - (sel.active ? sel.qty / sel.perQty : 0);
    const remainingQty = (this._maxSelect - portionsWithout) * sel.perQty;
    // domMax 为 Liquid 初始上限（含库存约束），取二者最小值
    const newMax = Math.min(sel.domMax ?? Infinity, remainingQty);
    stepperEl.dataset.max = String(Math.max(sel.perQty, newMax));
  }

  // 所有 stepper 统一刷新（某张卡片操作会影响其他卡片的剩余额度）
  // 同时同步每张 card 的 is-disabled class（多选模式达到限制时未选中的 card 不可点）
  _updateAllSteppersUI() {
    const isFull = this._totalPortions() >= this._maxSelect;
    const giftsCount = parseInt(this.dataset.giftsCount, 10) || this._selections.size;
    const isSingle = this._maxSelect <= 1 || giftsCount <= 1;
    this.classList.toggle('bgg--single-select', isSingle);
    for (const [giftId, sel] of this._selections) {
      this._updateStepperUI(giftId);
      // 更新 card 的 is-disabled class：非单选模式 + 总份数已满 + 当前未选中
      const card = this.querySelector(`.bgg-cart-card[data-gift-id="${giftId}"]`);
      if (card) {
        const disabled = !isSingle && isFull && !sel.active;
        card.classList.toggle('is-disabled', disabled);
      }
    }
  }

  disconnectedCallback() {
    if (this._ac) this._ac.abort();
  }
}

customElements.define('bgg-cart-modal', BggCartModal);


const noBorderHtml = `<button type="button" class="bgs__btn" data-action="dec" aria-label="decrease">
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M19.5 11.25C19.9142 11.25 20.25 11.5858 20.25 12C20.25 12.4142 19.9142 12.75 19.5 12.75H4.5C4.08579 12.75 3.75 12.4142 3.75 12C3.75 11.5858 4.08579 11.25 4.5 11.25H19.5Z" fill="#1A1A1A"/>
  </svg>
</button>
<input type="number" inputmode="numeric" class="bgs__qty" aria-label="quantity">
<button type="button" class="bgs__btn" data-action="inc" aria-label="increase">
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M12 3.75C12.4142 3.75 12.75 4.08579 12.75 4.5V11.25H19.5C19.9142 11.25 20.25 11.5858 20.25 12C20.25 12.4142 19.9142 12.75 19.5 12.75H12.75V19.5C12.75 19.9142 12.4142 20.25 12 20.25C11.5858 20.25 11.25 19.9142 11.25 19.5V12.75H4.5C4.08579 12.75 3.75 12.4142 3.75 12C3.75 11.5858 4.08579 11.25 4.5 11.25H11.25V4.5C11.25 4.08579 11.5858 3.75 12 3.75Z" fill="#1A1A1A"/>
  </svg>
</button>`;

const borderHtml = `<button type="button" class="bgs__btn" data-action="dec" aria-label="decrease">
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 12.75V11.25L17 11.25V12.75L7 12.75Z" fill="currentColor"/>
  </svg>
</button>
<input type="number" inputmode="numeric" class="bgs__qty" aria-label="quantity">
<button type="button" class="bgs__btn" data-action="inc" aria-label="increase">
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 12.75V11.25L17 11.25V12.75L7 12.75Z" fill="currentColor"/>
    <path d="M11.25 7L12.75 7L12.75 17L11.25 17L11.25 7Z" fill="currentColor"/>
  </svg>
</button>`;

// ── BggStepper custom element ────────────────────────────────────────────
class BggStepper extends HTMLElement {
  static get observedAttributes() {
    return ['data-value', 'data-min', 'data-max', 'data-disabled'];
  }

  connectedCallback() {
    this._render();
  }

  attributeChangedCallback() {
    this._render();
  }

  get _value() {
    return parseInt(this.dataset.value, 10) || 1;
  }
  get _min() {
    return parseInt(this.dataset.min, 10) || 1;
  }
  get _max() {
    return parseInt(this.dataset.max, 10) || 1;
  }
  get _step() {
    return parseInt(this.dataset.step, 10) || 1;
  }
  get _disabled() {
    return this.dataset.disabled === 'true';
  }

  _render() {
    const v = this._value;
    const atMin = v <= this._min || this._disabled;
    const atMax = v + this._step > this._max || this._disabled;

    // 首次创建或复用已有 DOM
    let decBtn = this.querySelector('.bgs__btn[data-action="dec"]');
    let qtyEl = this.querySelector('.bgs__qty');
    let incBtn = this.querySelector('.bgs__btn[data-action="inc"]');

    if (!decBtn) {
      const type = this.dataset.type || 'no-border';
      this.innerHTML = type === 'border' ? borderHtml : noBorderHtml;
      decBtn = this.querySelector('.bgs__btn[data-action="dec"]');
      qtyEl = this.querySelector('.bgs__qty');
      incBtn = this.querySelector('.bgs__btn[data-action="inc"]');

      decBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('bgg-stepper:dec', { bubbles: true }));
      });
      incBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent('bgg-stepper:inc', { bubbles: true }));
      });
      // 键盘直接输入：失焦或回车时提交，clamp 到 step 对齐的合法值
      const commit = (e) => {
        e.stopPropagation();
        const step = this._step;
        const raw = parseInt(qtyEl.value, 10);
        if (isNaN(raw) || raw < this._min) {
          // 恢复当前值
          qtyEl.value = String(this._value);
          return;
        }
        // 对齐到最近的 step 倍数，再 clamp
        const aligned = Math.round(raw / step) * step;
        const clamped = Math.max(this._min, Math.min(this._max, aligned));
        qtyEl.value = String(clamped);
        if (clamped !== this._value) {
          this.dispatchEvent(new CustomEvent('bgg-stepper:set', { bubbles: true, detail: { value: clamped } }));
        }
      };
      qtyEl.addEventListener('change', commit);
      qtyEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          qtyEl.blur();
          return;
        }
        // 只允许：数字、退格、删除、Tab、方向键、Home/End、Ctrl/Cmd 组合键
        const allowed =
          /^\d$/.test(e.key) ||
          [
            'Backspace',
            'Delete',
            'Tab',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Home',
            'End',
          ].includes(e.key) ||
          e.ctrlKey ||
          e.metaKey;
        if (!allowed) e.preventDefault();
      });
      qtyEl.addEventListener('click', (e) => e.stopPropagation());
    }

    qtyEl.value = String(v);
    qtyEl.min = String(this._min);
    qtyEl.max = String(this._max);
    qtyEl.step = String(this._step);
    decBtn.disabled = atMin;
    incBtn.disabled = atMax;
  }
}

customElements.define('bgg-stepper', BggStepper);


/* ===== VariantSwitcher — Standalone swatch-based variant selector ===== */
/*
 * <variant-switcher>
 *
 * A self-contained variant selector that renders inside any container
 * (e.g. a modal injected via Section Rendering API).
 * Mirrors the swatch + availability logic of PdpProductForm but operates
 * entirely within its own subtree — no <form> or URL side-effects.
 *
 * Dispatched events (bubble from the element):
 *   variant-switcher:change
 *     detail: { variant, oldVariant }
 *
 * Public API:
 *   element.currentVariant        — current resolved variant object (or null)
 *   element.variants              — full variants array
 *   element.getSelectedOptions()  → [{ index, value }, ...]
 */
class VariantSwitcher extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.variants = this._readVariants();
    if (!this.variants.length) return;

    this.currentVariant = this._resolveInitialVariant();

    this._bindSwatches();
    this._updateSwatchAvailability();
  }

  /* ── Variants data ── */

  _readVariants() {
    const giftId = this.dataset.giftId;

    // 1. Consolidated map: prefer BggCartModal.variantMapCache (already parsed),
    //    fall back to querying the DOM script element.
    //    Format: { "<giftId>": { handle, variants: [...] }, ... }
    if (giftId) {
      const map = BggCartModal.variantMapCache
      if (map) {
        const entry = map[giftId];
        if (entry && Array.isArray(entry.variants))
          return entry.variants;
      }
    }
    // 3. Legacy: [data-bgg-modal-variants] inside nearest B2G modal container
    const bggEl = this.querySelector('[data-bgg-modal-variants]');
    if (bggEl) {
      try { return JSON.parse(bggEl.textContent); } catch (_) { return []; }
    }

    return [];
  }

  _resolveInitialVariant() {
    const opts = this.getSelectedOptions();
    if (opts.length) {
      // 优先选有库存的精确匹配，回退到任意精确匹配
      const exactMatch = this.variants.find(v =>
        opts.every(o => v['option' + (o.index + 1)] === o.value)
      );
      if (exactMatch) return exactMatch;
    }
    // 无精确匹配时优先返回有库存的变体
    return this.variants.find(v => v.available) || this.variants[0] || null;
  }

  /* ── Swatch binding (mirrors PdpProductForm._bindSwatches) ── */

  _bindSwatches() {
    this.addEventListener('click', (e) => {
      const swatch = e.target.closest('[data-pdp-swatch]');
      if (!swatch) return;
      if (swatch.classList.contains('is-soldout') && !swatch.classList.contains('is-cross')) return;

      const optionEl = swatch.closest('[data-pdp-option]');
      if (!optionEl) return;

      optionEl.querySelectorAll('[data-pdp-swatch]').forEach(s => {
        s.classList.remove('is-active');
        if (s.hasAttribute('aria-pressed')) s.setAttribute('aria-pressed', 'false');
      });
      swatch.classList.add('is-active');
      if (swatch.hasAttribute('aria-pressed')) swatch.setAttribute('aria-pressed', 'true');

      const currentValueEl = optionEl.querySelector('[data-pdp-current-value]');
      if (currentValueEl) currentValueEl.textContent = swatch.dataset.value;

      this._onOptionChange();
    });
  }

  _onOptionChange() {
    const opts = this.getSelectedOptions();
    if (!opts.length) return;

    let newVariant = this.variants.find(v =>
      opts.every(o => v['option' + (o.index + 1)] === o.value)
    );
    if (!newVariant) return;

    // 若该组合缺货，尝试在保持刚选 option 不变的前提下找有库存的变体
    if (!newVariant.available) {
      // 找出本次点击改变的 option（与 currentVariant 不同的那一维）
      const changedOpt = this.currentVariant
        ? opts.find(o => this.currentVariant['option' + (o.index + 1)] !== o.value)
        : null;

      const fallback = this.variants.find(v => {
        if (!v.available) return false;
        // 保持本次选中的 option 值不变
        if (changedOpt && v['option' + (changedOpt.index + 1)] !== changedOpt.value) return false;
        return true;
      });

      if (fallback) {
        newVariant = fallback;
        // 同步其他 option 的 swatch active 状态到 fallback 的值
        const optionEls = Array.from(this.querySelectorAll('[data-pdp-option]'));
        optionEls.forEach(optionEl => {
          const idx = parseInt(optionEl.dataset.optionIndex, 10);
          const targetVal = fallback['option' + (idx + 1)];
          optionEl.querySelectorAll('[data-pdp-swatch]').forEach(s => {
            const active = s.dataset.value === targetVal;
            s.classList.toggle('is-active', active);
            if (s.hasAttribute('aria-pressed')) s.setAttribute('aria-pressed', String(active));
          });
          const currentValueEl = optionEl.querySelector('[data-pdp-current-value]');
          if (currentValueEl) currentValueEl.textContent = targetVal;
        });
      }
    }

    const oldVariant = this.currentVariant;
    this.currentVariant = newVariant;

    this._updateSwatchAvailability();

    this.dispatchEvent(new CustomEvent('variant-switcher:change', {
      bubbles: true,
      detail: { variant: newVariant, oldVariant },
    }));
  }

  /* ── Availability cross-check (mirrors PdpProductForm._updateSwatchAvailability) ── */

  _updateSwatchAvailability() {
    const optionEls = Array.from(this.querySelectorAll('[data-pdp-option]'));

    optionEls.forEach(optionEl => {
      const optionIndex = parseInt(optionEl.dataset.optionIndex, 10);
      const swatches = optionEl.querySelectorAll('[data-pdp-swatch]');

      swatches.forEach(swatch => {
        const testValue = swatch.dataset.value;

        // First pass: strict — fix other active dimensions
        const strictlyAvailable = this.variants.some(v => {
          if (v['option' + (optionIndex + 1)] !== testValue) return false;
          let matches = true;
          optionEls.forEach(otherEl => {
            const otherIndex = parseInt(otherEl.dataset.optionIndex, 10);
            if (otherIndex === optionIndex) return;
            const otherActive = otherEl.querySelector('[data-pdp-swatch].is-active');
            if (otherActive && v['option' + (otherIndex + 1)] !== otherActive.dataset.value) {
              matches = false;
            }
          });
          return matches && v.available;
        });

        if (strictlyAvailable) {
          swatch.classList.remove('is-soldout', 'is-cross');
          return;
        }

        // Second pass: loose — any combination with this value has stock
        const anyAvailable = this.variants.some(v =>
          v['option' + (optionIndex + 1)] === testValue && v.available
        );

        if (anyAvailable) {
          swatch.classList.remove('is-soldout');
          swatch.classList.add('is-cross');
        } else {
          swatch.classList.remove('is-cross');
          swatch.classList.add('is-soldout');
        }
      });
    });
  }

  /* ── Public API ── */

  getSelectedOptions() {
    const opts = [];
    this.querySelectorAll('[data-pdp-option]').forEach(optionEl => {
      const active = optionEl.querySelector('[data-pdp-swatch].is-active');
      if (active) {
        opts.push({
          index: parseInt(optionEl.dataset.optionIndex, 10),
          value: active.dataset.value,
        });
      }
    });
    return opts;
  }
}

customElements.define('variant-switcher', VariantSwitcher);

/* ===== BggVariantCard — syncs product image & price when variant-switcher changes ===== */
if (!customElements.get('bgg-variant-card')) {
  class BggVariantCard extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      // bgg-variant-card IS the root [data-bgg-variant-modal] — listen on self
      this.addEventListener('variant-switcher:change', (e) => this._onVariantChange(e.detail.variant));
    }

    _onVariantChange(variant) {
      if (!variant) return;

      // ── Image — supports both PDP modal ([data-bgg-modal-img]) and cart card (.bgg-cart-card__img) ──
      const imgEl = this.querySelector('[data-bgg-modal-img], .bgg-cart-card__img');
      if (imgEl) {
        const src = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
        if (src) {
          const sized = src.replace(/\.([^.]+)$/, '_162x200.$1');
          imgEl.src = sized;
          imgEl.alt = variant.featured_image?.alt || imgEl.alt;
        }
      }

      // ── Original price — supports .bgg-variant-modal__price-original and .bgg-cart-card__original-price ──
      const priceEl = this.querySelector('.bgg-variant-modal__price-original, .bgg-cart-card__original-price');
      if (priceEl) {
        if (variant.price > 0) {
          const fmt =
            window.T4SThemeSP?.Currency?.formatMoney
              ? window.T4SThemeSP.Currency.formatMoney(variant.price)
              : (variant.price / 100).toFixed(2);
          priceEl.textContent = fmt;
          priceEl.hidden = false;
        } else {
          priceEl.hidden = true;
        }
      }
    }
  }

  customElements.define('bgg-variant-card', BggVariantCard);
}

(function () {
  window.luteTheme = window.luteTheme || {}
  window.luteTheme.deferInitialCartReconcile = true

  if (!window.luteTheme.cart || !window.luteTheme.cart.getCart) {
    var cartSnapshot = null
    var cartSnapshotUpdatedAt = 0
    var cartFetchPromise = null
    var cartFetchRevision = 0
    var cartRevision = 0
    var CART_CACHE_TTL = 1000

    function getRoute(path) {
      var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'
      return root + path
    }

    window.luteTheme.cart = {
      getCart: function (options) {
        var force = options && options.force
        var now = Date.now()

        if (cartFetchPromise && cartFetchRevision === cartRevision) return cartFetchPromise
        if (!force && cartSnapshot && now - cartSnapshotUpdatedAt < CART_CACHE_TTL) return Promise.resolve(cartSnapshot)

        var fetchRevision = cartRevision
        cartFetchRevision = fetchRevision

        var nextFetchPromise = fetch(getRoute('cart.js'), {
          credentials: 'same-origin',
        })
          .then(function (response) {
            if (!response.ok) throw response
            return response.json()
          })
          .then(function (cart) {
            if (fetchRevision === cartRevision) window.luteTheme.cart.update(cart)
            return cart
          })
          .finally(function () {
            if (cartFetchPromise === nextFetchPromise) cartFetchPromise = null
          })

        cartFetchPromise = nextFetchPromise

        return cartFetchPromise
      },
      update: function (cart) {
        if (!cart || typeof cart !== 'object' || !Array.isArray(cart.items)) return

        cartSnapshot = cart
        cartSnapshotUpdatedAt = Date.now()
      },
      invalidate: function () {
        cartRevision += 1
        cartSnapshotUpdatedAt = 0
      },
    }
  }

  window.luteTheme.loadFeature = function (options) {
    var configElement = options.configElement || (options.configId ? document.getElementById(options.configId) : null)
    var promiseKey = options.promiseKey
    var requireContent = options.requireContent !== false

    if (configElement) {
      if (requireContent && !configElement.textContent.trim()) return promiseKey ? window[promiseKey] : null

      options.src = options.src || configElement.dataset.src
    }

    if (!options.src) return promiseKey ? window[promiseKey] : null

    if (!promiseKey) promiseKey = '__luteFeaturePromise:' + options.src

    window[promiseKey] ||= import(options.src).catch(function (error) {
      window[promiseKey] = null
      throw error
    })

    return window[promiseKey]
  }

  window.luteTheme.features = window.luteTheme.features || [
    { configId: 't4s-gwp-config', promiseKey: '__cartGwpPromise' },
    { configId: 't4s-b2g-config', promiseKey: '__cartB2gPromise' },
  ]

  window.luteTheme.runInitialCartReconcilers = function () {
    if (window.luteTheme.initialCartReconcilePromise) return window.luteTheme.initialCartReconcilePromise

    window.luteTheme.initialCartReconcilePromise = Promise.resolve().then(function () {
      var reconcilers = [window.luteTheme.b2g, window.luteTheme.gwp]

      return Promise.all(reconcilers.map(function (feature) {
        return feature && typeof feature.reconcile === 'function' ? feature.reconcile() : null
      }))
    })

    return window.luteTheme.initialCartReconcilePromise
  }

  window.luteTheme.loadConfiguredFeatures = function (features) {
    var promises = (features || window.luteTheme.features || []).map(function (feature) {
      return window.luteTheme.loadFeature(feature)
    })

    Promise.all(promises.map(function (promise) {
      return Promise.resolve(promise).catch(function () { return null })
    })).then(window.luteTheme.runInitialCartReconcilers)

    return promises
  }

  window.luteTheme.init = function () {
    window.luteTheme.loadConfiguredFeatures()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.luteTheme.init, { once: true })
  } else {
    window.luteTheme.init()
  }

  document.addEventListener('shopify:section:load', window.luteTheme.init)
  document.addEventListener('cart:updated', window.luteTheme.init)
  document.addEventListener('cart:refresh:opened', window.luteTheme.init)
})()
